import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { Loader2, Plus, Download } from "lucide-react";
import { listInvoices } from "@/lib/accounting/accounting.functions";
import { downloadCSV, fmtMoney, fmtDate, toCSV } from "@/lib/accounting/exports";

export const Route = createFileRoute("/_authenticated/admin/accounting/invoices/")({
  component: InvoicesList,
});

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  sent: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  viewed: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  partially_paid: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  overdue: "bg-red-500/15 text-red-300 border-red-500/30",
  void: "bg-zinc-500/10 text-zinc-500 border-zinc-500/20 line-through",
};

function InvoicesList() {
  const fn = useServerFn(listInvoices);
  const { data, isLoading } = useQuery({ queryKey: ["accounting", "invoices"], queryFn: () => fn() });
  const [filter, setFilter] = useState<string>("all");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const rows = data ?? [];
    return rows.filter((r: any) => {
      if (filter !== "all" && r.status !== filter) return false;
      if (q) {
        const s = q.toLowerCase();
        return (
          r.invoice_number.toLowerCase().includes(s) ||
          (r.customer_name || "").toLowerCase().includes(s) ||
          (r.property_address || "").toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [data, filter, q]);

  const exportCsv = () => {
    const rows = filtered.map((r: any) => ({
      invoice_number: r.invoice_number,
      customer: r.customer_name,
      address: r.property_address,
      issue_date: r.issue_date,
      due_date: r.due_date,
      status: r.status,
      subtotal: r.subtotal,
      tax: r.tax_amount,
      total: r.total,
      paid: r.amount_paid,
      balance_due: r.balance_due,
    }));
    downloadCSV(`invoices_${new Date().toISOString().slice(0, 10)}.csv`, toCSV(rows));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1">
          {["all", "draft", "sent", "viewed", "partially_paid", "paid", "overdue", "void"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border ${
                filter === s ? "bg-white text-zinc-950 border-white" : "border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              {s.replace("_", " ")}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white placeholder:text-zinc-500 w-48"
          />
          <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-zinc-200 hover:bg-white/5">
            <Download className="size-3.5" /> CSV
          </button>
          <Link to="/admin/accounting/invoices/new" search={{ projectId: undefined }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-300 text-zinc-950 text-xs font-semibold">
            <Plus className="size-3.5" /> New invoice
          </Link>
        </div>
      </div>

      {isLoading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
              <tr>
                <th className="text-left px-4 py-3">Invoice</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Issued</th>
                <th className="text-left px-4 py-3">Due</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-right px-4 py-3">Balance</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-zinc-500">No invoices found.</td></tr>
              ) : filtered.map((r: any) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <Link to="/admin/accounting/invoices/$id" params={{ id: r.id }} className="text-amber-300 font-mono text-xs">{r.invoice_number}</Link>
                  </td>
                  <td className="px-4 py-3 text-white">{r.customer_name}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{fmtDate(r.issue_date)}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{fmtDate(r.due_date)}</td>
                  <td className="px-4 py-3 text-right text-white">{fmtMoney(r.total)}</td>
                  <td className={`px-4 py-3 text-right ${Number(r.balance_due) > 0 ? "text-amber-300" : "text-emerald-300"}`}>{fmtMoney(r.balance_due)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-medium rounded-full border ${STATUS_COLORS[r.status] || ""}`}>
                      {r.status.replace("_", " ")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}